import { gql, useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router";
import FormRenderer from "./FormRenderer";
import { useEffect, useMemo, useRef } from "react";
import LargeLoader from "./LargeLoader";
import { useFeedback } from "./feedback/Service";
import { parseGraphQLError } from "../utils";
import useUser from "./auth/useUser";
import { Typography } from "@mui/joy";

const GET_QUESTIONNAIRE = gql`
  query getQuestions($jobId: String!, $clientId: String!) {
    getQuestions(jobId: $jobId, clientId: $clientId)
  }
`;

const POST_ANSWERS = gql`
  mutation postAnswers($jobId: String!, $clientId: String!, $answers: String!) {
    postAnswers(jobId: $jobId, clientId: $clientId, answers: $answers) {
      _id
      questionnaire {
        _id
        passed
      }
    }
  }
`;

const Questionnaire = () => {
  const { jobId, clientId } = useParams();
  const submitCalled = useRef(false);
  const { data, loading, error } = useQuery(GET_QUESTIONNAIRE, {
    variables: { jobId, clientId },
    skip: !jobId || !clientId,
  });
  const { user, refetch } = useUser();

  const feedback = useFeedback();
  const navigate = useNavigate();

  const [postAnswers, { loading: posting }] = useMutation(POST_ANSWERS);

  const handleSubmit = async (answers) => {
    submitCalled.current = true;
    // console.log("handleSubmit", answers);
    try {
      const { data: { postAnswers: result } } = await postAnswers({
        variables: {
          jobId,
          clientId,
          answers: JSON.stringify(answers),
        },
      });
      console.log("postAnswers", result);
      feedback.snackbar({ text: "Answers submitted successfully", type: "success" });
      window.gtag('event', 'questionnaire_finished', {});
      refetch({ fetchPolicy: 'network-only' });
      // const result = { questionnaire: { passed: false } }; // Mock result for testing
      if (!result.questionnaire.passed) {
        await feedback.alert({ title: "All Done!", text: "Thanks for completing the questions. The company may follow up if there's a good match." });
      }
      navigate(result.questionnaire.passed ? `/company/${clientId}/job/${jobId}/video-interview-intro` : `/applications`);
    } catch (error) {
      console.error("postAnswers error", error);
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  }

  const questions = useMemo(() => {
    if (!data) return [];
    const parsed = JSON.parse(data.getQuestions);
    return parsed;
  }, [data]);

  useEffect(() => {
    if (!loading && data && questions.length === 0) {
      navigate(`/company/${clientId}/job/${jobId}/video-interview-intro`, { replace: true });
    }
  }, [loading, data, questions.length, navigate, clientId, jobId]);

  useEffect(() => {
    if (!user) return;
    if (submitCalled.current) return;
    const application = user.talent.applications.find((app) => app.job._id === jobId && app.client._id === clientId);
    if (application?.questionnaire) {
      console.log("You have already submitted the questionnaire");
      feedback.snackbar({ text: "You have already submitted the questionnaire", type: "error" });
      navigate("/applications");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, jobId, clientId]);

  // console.log("questions", questions, error, loading);
  if (error) return <Typography level="body-lg" sx={{ p: 4 }}>{parseGraphQLError(error)}</Typography>;
  if (loading) return <LargeLoader />;
  if (questions.length === 0) return <LargeLoader />;
  return (
    <>
      <FormRenderer form={{ questions }} onSubmit={handleSubmit} isPosting={posting} />
    </>
  );
}

export default Questionnaire;
